/**
 * Transforms a flat array of rollup filter configs into a hierarchical tree.
 *
 * Folders are created for datasets with valuePath items (rollupValuePathType
 * or nestedRollupTagType with a main.* terminal). Tag-chain rollups
 * (isTagRollup: true) render as indented "tagFilter" leaf nodes, using
 * pre-computed levels from parentFilterId (set by ootils).
 *
 * @param {Array} rollups - Flat array of rollup filter configs (with isTagRollup flag from ootils)
 * @param {Object} rollupDisplayMap - Map of contentType → display title
 * @param {Object} levelMap - Pre-computed filter level map from computeFilterLevels
 * @returns {Array<RollupTreeNode>} Tree of folder, filter, and tagFilter nodes
 *
 * Node shapes:
 *   { type: 'folder', contentType, display, ownFilters, tagFilters, rollupChildren }
 *   { type: 'filter', filter: RollupFilterConfig }
 *   { type: 'tagFilter', filter: RollupFilterConfig, level: number }
 */
export const buildRollupTree = (rollups, rollupDisplayMap = {}, levelMap = {}) => {
  if (!rollups?.length) return [];

  const getDisplay = (contentType) =>
    rollupDisplayMap[contentType] || contentType;

  // Determine which contentTypes need folder nodes (only non-tag rollups create folders)
  const folderContentTypes = new Set();

  for (const item of rollups) {
    if (item.isTagRollup) continue;
    const target = item.target;
    if (target?.filterType === "rollupValuePathType") {
      if (target.tagTypeCollectionToRollup) {
        folderContentTypes.add(target.tagTypeCollectionToRollup);
      }
    } else if (target?.rollupPath?.length) {
      for (let i = 0; i < target.rollupPath.length - 1; i++) {
        folderContentTypes.add(target.rollupPath[i]);
      }
    }
  }

  // Ensure intermediate hops leading to a folder are also folders
  for (const item of rollups) {
    if (item.isTagRollup) continue;
    const rp = item.target?.rollupPath;
    if (!rp?.length) continue;
    for (let i = rp.length - 1; i >= 0; i--) {
      if (folderContentTypes.has(rp[i])) {
        for (let j = 0; j < i; j++) folderContentTypes.add(rp[j]);
        break;
      }
    }
  }

  const buildLevel = (items, pathIndex) => {
    const folders = new Map();
    const tagItems = [];
    const valuePathLeaves = [];

    for (const item of items) {
      const rollupPath = item.target?.rollupPath;

      if (item.isTagRollup) {
        if (rollupPath && pathIndex < rollupPath.length - 1) {
          const nextHop = rollupPath[pathIndex];
          if (folderContentTypes.has(nextHop)) {
            if (!folders.has(nextHop)) folders.set(nextHop, []);
            folders.get(nextHop).push(item);
          } else {
            tagItems.push(item);
          }
        } else {
          tagItems.push(item);
        }
      } else if (rollupPath) {
        if (pathIndex >= rollupPath.length - 1) {
          valuePathLeaves.push(item);
        } else {
          const nextHop = rollupPath[pathIndex];
          if (folderContentTypes.has(nextHop)) {
            if (!folders.has(nextHop)) folders.set(nextHop, []);
            folders.get(nextHop).push(item);
          } else {
            valuePathLeaves.push(item);
          }
        }
      } else if (item.target?.filterType === "rollupValuePathType") {
        const parent = item.target.tagTypeCollectionToRollup;
        if (pathIndex === 0 && parent && folderContentTypes.has(parent)) {
          if (!folders.has(parent)) folders.set(parent, []);
          folders.get(parent).push(item);
        } else {
          valuePathLeaves.push(item);
        }
      } else {
        valuePathLeaves.push(item);
      }
    }

    // Terminal merge: valuePath leaves matching a folder go inside it
    const finalValuePathLeaves = [];
    for (const item of valuePathLeaves) {
      const rollupPath = item.target?.rollupPath;
      const terminalCt = rollupPath?.[rollupPath.length - 1];
      if (terminalCt && folders.has(terminalCt)) {
        folders.get(terminalCt).push(item);
      } else {
        finalValuePathLeaves.push({ type: "filter", filter: item });
      }
    }

    // Build folder nodes
    const folderNodes = [];
    for (const [ct, children] of folders) {
      const childNodes = buildLevel(children, pathIndex + 1);
      if (childNodes.length === 0) continue;

      const ownFilters = [];
      const tagFilters = [];
      const rollupChildren = [];

      for (const node of childNodes) {
        if (node.type === "folder") {
          rollupChildren.push(node);
        } else if (node.type === "tagFilter") {
          const rp = node.filter.target?.rollupPath;
          const lastEl = rp?.[rp.length - 1];
          if (lastEl === ct) continue;
          tagFilters.push(node);
        } else if (node.type === "filter") {
          const rp = node.filter.target?.rollupPath;
          const lastEl = rp?.[rp.length - 1];
          if (lastEl === ct) continue;
          ownFilters.push(node);
        }
      }

      if (ownFilters.length === 0 && tagFilters.length === 0 && rollupChildren.length === 0) {
        continue;
      }

      folderNodes.push({
        type: "folder",
        contentType: ct,
        display: getDisplay(ct),
        ownFilters,
        tagFilters,
        rollupChildren,
      });
    }

    // Tag items: indentation computed LOCALLY among these sibling tag items
    // only. A tag is indented under its parent only when that parent renders
    // as a sibling in THIS same folder. Parents that live in another folder
    // (deep rollup chains — e.g. tten's "Select the respondent" links, where
    // each dataset's parent sits one folder deeper) must NOT leak indentation
    // here. The global `levelMap` is folder-agnostic, so it is intentionally
    // not used for this. Relies on parent-before-child order, guaranteed by
    // ootils' sortFiltersHierarchically.
    const tagItemIds = new Set(tagItems.map((i) => i.filterId));
    const localLevel = {};
    const tagFilterNodes = tagItems.map((item) => {
      const parentId = item.parentFilterId;
      const level =
        parentId && tagItemIds.has(parentId) ? (localLevel[parentId] ?? 0) + 1 : 0;
      localLevel[item.filterId] = level;
      return { type: "tagFilter", filter: item, level };
    });

    return [...folderNodes, ...tagFilterNodes, ...finalValuePathLeaves];
  };

  return buildLevel(rollups, 0);
};
